import { useSyncExternalStore } from "react";
import {
  ConsentCategory,
  ConsentChoices,
  ConsentConfig,
  ConsentRecord,
  ConsentSource,
  ConsentState,
} from "../types";
import { readConsentConfig } from "../utils/consentConfig";
import {
  buildConsentRecord,
  clearAnalyticsStorage,
  clearMarketingStorage,
  parseCrossDomainConsent,
  readConsentRecord,
  recordAppliesToPolicy,
  recordAppliesToTenant,
  writeConsentRecord,
} from "../utils/consentCookie";
import { readVisitorId } from "../../Tracking/utils/visitorId";

/**
 * THE consent store: one answer to "may analytics run, may marketing run" for
 * the whole web app, read by every tracking consumer (Datadog, the tracker,
 * visitor linking, Google Tag Manager, campaign parameter persistence).
 *
 * Module state rather than React context or Recoil, for the same reason as
 * `telemetryLevelStore`: the Datadog setup reads it at module scope before
 * React exists, and the consumers are mounted in different places in the two
 * apps (GTM wraps the portal's provider tree; the standalone token pages
 * return before it). Built on `useSyncExternalStore`, so any component can
 * subscribe with no ordering requirement.
 *
 * Inputs, in order of precedence (OptIn mode only):
 *
 * 1. A `dapple_consent` record (cookie, then localStorage) for the CURRENT
 *    policy version. Written by the portal banner, or by `visitor.js` on the
 *    clinic's website when it shares the registrable domain.
 * 2. The cross-domain hand-off `?dapple_consent=10` that `visitor.js` puts on
 *    links into the portal, adopted once at boot and written to the cookie.
 * 3. Global Privacy Control (`navigator.globalPrivacyControl === true`), which
 *    counts as Reject all with no banner.
 * 4. Otherwise nothing is decided and both categories are off.
 *
 * The store also listens for the `dapple:consent` DOM event, which `visitor.js`
 * fires whenever consent changes (including when it translates a clinic's own
 * consent tool in OwnTool mode), and fires the same event itself when the
 * portal banner records a decision.
 *
 * NotRequired mode (the default, and what every page without the backend's
 * injected config gets: native, the clinic app, local dev) is today's
 * behaviour: analytics and marketing both allowed, nothing to decide, no
 * banner. The device telemetry level and the LaunchDarkly kill switch still
 * apply on top; see `useTrackingAllowed`.
 */

/** The DOM event `visitor.js` and the portal banner fire on consent changes. */
export const CONSENT_EVENT = "dapple:consent";

/** The global `visitor.js` defines in every mode. */
export type DappleTrackingApi = {
  grant?: (
    choices: ConsentChoices & {
      source?: ConsentSource;
      withdrawnVisitorId?: string;
    },
  ) => void;
  deny?: (options?: {
    source?: ConsentSource;
    withdrawnVisitorId?: string;
  }) => void;
  openSettings?: () => void;
  getConsent?: () => ConsentChoices & { decided: boolean };
};

type ConsentWindow = Window & {
  dappleTracking?: DappleTrackingApi;
};

/** Everything a subscriber can read, as one immutable snapshot. */
export type ConsentStoreSnapshot = {
  config: ConsentConfig;
  state: ConsentState;
  /** Whether the "Cookie settings" panel has been asked to open. */
  settingsOpen: boolean;
  /** Whether the visitor made a choice through the banner on this page load. */
  decidedThisPage: boolean;
};

const consentWindow = (): ConsentWindow | undefined => {
  try {
    return globalThis.window as ConsentWindow | undefined;
  } catch {
    return undefined;
  }
};

const readGlobalPrivacyControl = (): boolean => {
  try {
    const nav = globalThis.navigator as
      | (Navigator & { globalPrivacyControl?: unknown })
      | undefined;
    return nav?.globalPrivacyControl === true;
  } catch {
    return false;
  }
};

const readSearch = (): string => {
  try {
    return globalThis.location?.search ?? "";
  } catch {
    return "";
  }
};

/** The referring page's hostname, lower case, or "" when there is none. */
const readReferrerHost = (): string => {
  try {
    const referrer = globalThis.document?.referrer ?? "";
    return referrer ? new URL(referrer).hostname.toLowerCase() : "";
  } catch {
    return "";
  }
};

/** Same host, or its `www.` twin. */
const sameSite = (a: string, b: string): boolean =>
  a.replace(/^www\./, "") === b.replace(/^www\./, "");

/**
 * Whether a consent choice handed over on a link can be applied. Anyone can
 * craft `?dapple_consent=11`, so a GRANT counts only when the visitor came
 * from one of the tenant's own hosts (the clinic's website, where they chose
 * in its banner). A denial is applied from anywhere.
 */
export const canAdoptHandedOverConsent = (
  choices: ConsentChoices,
  handoffHosts: readonly string[] | undefined,
  referrerHost: string,
): boolean => {
  if (!choices.analytics && !choices.marketing) return true;
  if (!referrerHost || !handoffHosts?.length) return false;
  return handoffHosts.some(host => sameSite(host, referrerHost));
};

/**
 * The rule that turns the inputs into an answer. Pure, so it is tested
 * directly and can never disagree between boot and runtime.
 */
export const computeConsentState = (
  config: ConsentConfig,
  record: ConsentRecord | null,
  globalPrivacyControl: boolean,
  tenant?: string,
): ConsentState => {
  const base = { mode: config.mode, banner: config.banner };

  if (config.mode === "NotRequired") {
    return { ...base, analytics: true, marketing: true, decided: true };
  }

  if (
    recordAppliesToPolicy(record, config.policyVersion) &&
    recordAppliesToTenant(record, tenant)
  ) {
    return { ...base, analytics: record.a, marketing: record.m, decided: true };
  }

  if (globalPrivacyControl) {
    return { ...base, analytics: false, marketing: false, decided: true };
  }

  return { ...base, analytics: false, marketing: false, decided: false };
};

/** Whether the Dapple banner should ask this visitor. */
export const needsConsentBanner = (state: ConsentState): boolean =>
  state.mode === "OptIn" && state.banner === "DappleOS" && !state.decided;

let snapshot: ConsentStoreSnapshot;
const listeners = new Set<() => void>();

/**
 * The tenant this portal belongs to: the injected `tenant` when the backend
 * provides it, otherwise whatever the portal reports from its own config via
 * `setConsentTenant`. Records for any other tenant are ignored.
 */
let currentTenant: string | undefined;

/**
 * A decision made before the tenant was known. Written without `o` would make
 * it a record no page honours, so it waits here and is written, stamped, the
 * moment `setConsentTenant` supplies the tenant.
 */
let pendingStamp:
  | { choices: ConsentChoices; policyVersion: string; source: ConsentSource }
  | undefined;

/**
 * Writes a record stamped with the current tenant, or holds it until the
 * tenant is known.
 */
const writeStampedRecord = (
  choices: ConsentChoices,
  policyVersion: string,
  source: ConsentSource,
) => {
  if (!currentTenant) {
    pendingStamp = { choices, policyVersion, source };
    return;
  }
  pendingStamp = undefined;
  writeConsentRecord(
    buildConsentRecord(
      choices,
      policyVersion,
      source,
      Date.now(),
      currentTenant,
    ),
  );
};

const notify = () => {
  listeners.forEach(listener => listener());
};

/**
 * Applies the contract's "on withdraw" rule: when a category goes from allowed
 * to not allowed, its storage goes with it. `visitor.js` does the same on its
 * side; doing it here as well covers a page where it never loaded.
 */
const clearWithdrawnStorage = (previous: ConsentState, next: ConsentState) => {
  if (next.mode !== "OptIn") return;
  if (previous.analytics && !next.analytics) clearAnalyticsStorage();
  if (previous.marketing && !next.marketing) clearMarketingStorage();
};

const setSnapshot = (
  patch: Partial<ConsentStoreSnapshot>,
  { clearWithdrawn = true }: { clearWithdrawn?: boolean } = {},
) => {
  const next = { ...snapshot, ...patch };
  const stateChanged =
    next.state.analytics !== snapshot.state.analytics ||
    next.state.marketing !== snapshot.state.marketing ||
    next.state.decided !== snapshot.state.decided;
  const changed =
    stateChanged ||
    next.settingsOpen !== snapshot.settingsOpen ||
    next.decidedThisPage !== snapshot.decidedThisPage ||
    next.config !== snapshot.config;

  if (!changed) return;

  const previousState = snapshot.state;
  snapshot = stateChanged ? next : { ...next, state: snapshot.state };
  if (stateChanged && clearWithdrawn) {
    clearWithdrawnStorage(previousState, snapshot.state);
  }
  notify();
};

/** What the stored record says right now, for the current tenant. */
const stateFromStorage = (): ConsentState =>
  computeConsentState(
    snapshot.config,
    readConsentRecord(),
    readGlobalPrivacyControl(),
    currentTenant,
  );

const isChoicesDetail = (
  detail: unknown,
): detail is ConsentChoices & { decided?: boolean } =>
  typeof detail === "object" &&
  detail !== null &&
  typeof (detail as ConsentChoices).analytics === "boolean" &&
  typeof (detail as ConsentChoices).marketing === "boolean";

/**
 * `dapple:consent` from `visitor.js` (or our own banner). The cookie is the
 * source of truth; the event detail is used only when the cookie could not be
 * read (cookies and storage blocked), so the page still follows the choice.
 */
const handleConsentEvent = (event: Event) => {
  if (snapshot.config.mode !== "OptIn") return;

  // A decision this page made before the tenant was known is held in memory
  // until it can be written stamped. The stored record is older than it, so
  // re-reading storage here would let a past acceptance overwrite a fresh
  // rejection. The in-memory choice stands until setConsentTenant writes it.
  if (pendingStamp) return;

  // Never fall back to "undecided" on an event: a record this page cannot
  // honour (an older writer with no tenant) must not flash the banner or
  // trigger the withdrawal cleanup. Only a decided answer is applied.
  const fromStorage = stateFromStorage();
  if (fromStorage.decided) {
    setSnapshot({ state: fromStorage });
    return;
  }

  const detail = (event as CustomEvent<unknown>).detail;
  if (!isChoicesDetail(detail) || detail.decided === false) return;

  setSnapshot({
    state: {
      ...snapshot.state,
      analytics: detail.analytics,
      marketing: detail.marketing,
      decided: true,
    },
  });
};

let listening = false;

const startListening = () => {
  const win = consentWindow();
  if (listening || typeof win?.addEventListener !== "function") return;
  win.addEventListener(CONSENT_EVENT, handleConsentEvent);
  listening = true;
};

const stopListening = () => {
  const win = consentWindow();
  if (!listening || typeof win?.removeEventListener !== "function") return;
  win.removeEventListener(CONSENT_EVENT, handleConsentEvent);
  listening = false;
};

/**
 * Builds the store from the page. Runs once at import (synchronously, so the
 * Datadog setup sees the answer before it initialises the SDK) and again from
 * the test seam.
 */
const initialise = () => {
  const config = readConsentConfig();
  currentTenant = config.tenant;
  pendingStamp = undefined;
  const record = readConsentRecord();

  let state = computeConsentState(
    config,
    record,
    readGlobalPrivacyControl(),
    currentTenant,
  );

  if (config.mode === "OptIn" && !state.decided) {
    // The visitor chose on the clinic's website, on another registrable
    // domain, and `visitor.js` handed the choice over on the link.
    const handedOver = parseCrossDomainConsent(readSearch());
    if (
      handedOver &&
      canAdoptHandedOverConsent(handedOver, config.handoffHosts, readReferrerHost())
    ) {
      state = { ...state, ...handedOver, decided: true };
      writeStampedRecord(handedOver, config.policyVersion, "link");
    }
  }

  snapshot = {
    config,
    state,
    settingsOpen: false,
    decidedThisPage: false,
  };

  if (config.mode === "OptIn") {
    // "Before consent: no dapple_* tracking cookies or storage." A brand that
    // has just moved from NotRequired to OptIn leaves earlier ones behind.
    if (!state.analytics) clearAnalyticsStorage();
    if (!state.marketing) clearMarketingStorage();
    startListening();
  }
};

initialise();

/** The current snapshot. Safe to call from anywhere, including at boot. */
export const getConsentSnapshot = (): ConsentStoreSnapshot => snapshot;

/** The current consent answer. */
export const getConsentState = (): ConsentState => snapshot.state;

/** Whether a category is consented to right now (ignores the telemetry level). */
export const hasConsent = (category: ConsentCategory): boolean =>
  snapshot.state[category];

/** Subscribes to every change. Returns the unsubscribe function. */
export const subscribeConsent = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Reports the tenant this portal belongs to, from its own config, for pages
 * where the backend did not inject one. Records for any other tenant are then
 * ignored, and the answer is re-checked against the stored record.
 */
export const setConsentTenant = (tenant: string | null | undefined): void => {
  const next = tenant || undefined;
  if (!next || next === currentTenant) return;
  // The injected tenant is authoritative; the portal's own is only a fallback.
  if (snapshot.config.tenant) return;

  currentTenant = next;

  // A decision made before the tenant was known: write it now, stamped. The
  // answer in memory is already that decision, so there is nothing to re-check.
  if (pendingStamp) {
    const { choices, policyVersion, source } = pendingStamp;
    writeStampedRecord(choices, policyVersion, source);
    return;
  }

  if (snapshot.config.mode !== "OptIn" || snapshot.decidedThisPage) return;

  // Re-check without the withdrawal cleanup: a record this page cannot
  // honour is "not decided", not a withdrawal by this visitor.
  setSnapshot({ state: stateFromStorage() }, { clearWithdrawn: false });
};

/** The tenant records are currently checked against (test and debug aid). */
export const getConsentTenant = (): string | undefined => currentTenant;

const dispatchConsentEvent = (choices: ConsentChoices) => {
  try {
    const win = consentWindow();
    if (
      typeof win?.dispatchEvent === "function" &&
      typeof CustomEvent === "function"
    ) {
      win.dispatchEvent(
        new CustomEvent(CONSENT_EVENT, {
          detail: { ...choices, decided: true },
        }),
      );
    }
  } catch {
    // An event that cannot be dispatched must never undo a recorded choice.
  }
};

/**
 * Phase one of a decision: every subscriber follows the new answer at once
 * (Datadog, GTM, the tracker loader, the banner closes), but NOTHING is stored
 * or cleared yet. See `decideConsentFromBanner` for why.
 */
const applyDecisionInMemory = (choices: ConsentChoices) => {
  setSnapshot(
    {
      state: {
        ...snapshot.state,
        analytics: choices.analytics,
        marketing: choices.marketing,
        decided: true,
      },
      settingsOpen: false,
      decidedThisPage: true,
    },
    { clearWithdrawn: false },
  );
};

/**
 * Phase two: writes the cookie (with this tenant as `o`), applies the
 * withdrawal cleanup against the answer that held BEFORE the decision, and
 * fires `dapple:consent` for anything else on the page.
 */
const persistDecision = (
  choices: ConsentChoices,
  source: ConsentSource,
  before: ConsentState,
) => {
  writeStampedRecord(choices, snapshot.config.policyVersion, source);
  clearWithdrawnStorage(before, { ...before, ...choices, decided: true });
  dispatchConsentEvent(choices);
};

/**
 * Records a decision immediately: in memory, then the cookie and the
 * withdrawal cleanup. For decisions that need no proof-of-consent record from
 * `visitor.js`; banner decisions go through `decideConsentFromBanner`.
 *
 * A no-op outside OptIn: there is nothing to decide in NotRequired mode.
 */
export const recordConsentDecision = (
  choices: ConsentChoices,
  source: ConsentSource = "banner",
): void => {
  if (snapshot.config.mode !== "OptIn") return;

  const before = snapshot.state;
  applyDecisionInMemory(choices);
  persistDecision(choices, source, before);
};

/** Extra detail handed to `visitor.js` with a banner decision. */
type TrackerDecisionDetail = {
  /** The visitor id being withdrawn, read BEFORE anything clears it. */
  withdrawnVisitorId?: string;
};

/**
 * Hands the decision to `visitor.js` if it is running right now, so it records
 * the consent event (the proof-of-consent record) and starts or stops itself:
 * `deny({source: "banner", withdrawnVisitorId})` for Reject all, otherwise
 * `grant({analytics, marketing, source: "banner"})` (with `withdrawnVisitorId`
 * when analytics is being withdrawn). Returns false when the tracker is not
 * (yet) on the page.
 */
export const forwardDecisionToTracker = (
  choices: ConsentChoices,
  { withdrawnVisitorId }: TrackerDecisionDetail = {},
): boolean => {
  try {
    const api = consentWindow()?.dappleTracking;
    if (!api) return false;

    const withdrawn = withdrawnVisitorId ? { withdrawnVisitorId } : {};

    if (
      !choices.analytics &&
      !choices.marketing &&
      typeof api.deny === "function"
    ) {
      api.deny({ source: "banner", ...withdrawn });
      return true;
    }
    if (typeof api.grant === "function") {
      api.grant({ ...choices, source: "banner", ...withdrawn });
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

/** How often, and for how long, to wait for `visitor.js` to define its API. */
export const TRACKER_POLL_INTERVAL_MS = 250;
export const TRACKER_POLL_TIMEOUT_MS = 10_000;

let cancelPendingDelivery: (() => void) | undefined;

/**
 * Calls `onDelivered` once the decision has been handed to `visitor.js`, or
 * once it is clear it never will be (the timeout). Polls briefly while
 * `useVisitorTrackerScript` injects the tracker. A newer decision replaces a
 * pending one, so only the latest choice is delivered. Returns a cancel
 * function, which does NOT call `onDelivered`.
 */
export const deliverDecisionToTracker = (
  choices: ConsentChoices,
  detail: TrackerDecisionDetail = {},
  onSettled: () => void = () => undefined,
): (() => void) => {
  cancelPendingDelivery?.();
  cancelPendingDelivery = undefined;

  if (forwardDecisionToTracker(choices, detail)) {
    onSettled();
    return () => undefined;
  }

  const startedAt = Date.now();
  const timer = setInterval(() => {
    const delivered = forwardDecisionToTracker(choices, detail);
    if (delivered || Date.now() - startedAt >= TRACKER_POLL_TIMEOUT_MS) {
      cancel();
      onSettled();
    }
  }, TRACKER_POLL_INTERVAL_MS);

  function cancel() {
    clearInterval(timer);
    if (cancelPendingDelivery === cancel) cancelPendingDelivery = undefined;
  }

  cancelPendingDelivery = cancel;
  return cancel;
};

let flushPendingPersist: (() => void) | undefined;

/**
 * A banner decision, in the order the proof-of-consent record needs.
 *
 * `visitor.js` records a consent event only for a decision it has not seen.
 * If the portal wrote the cookie first, the tracker would load, find a
 * decision already stored, and record nothing; and on a withdrawal the visitor
 * id would be gone before the tracker could say whose consent was withdrawn.
 * So:
 *
 * 1. Read the visitor id now, if analytics is being withdrawn.
 * 2. Apply the answer in memory, so every consumer follows at once and the
 *    tracker loader injects `visitor.js` (never on token routes).
 * 3. Hand the decision to `visitor.js` (`grant`/`deny`, source "banner").
 * 4. Only then write the cookie and clear withdrawn storage. Also on the
 *    delivery timeout, and when the page is hidden first, so a decision is
 *    never lost to navigation. When no tracker is expected (a token route, or
 *    telemetry switched off) it persists straight away.
 */
export const decideConsentFromBanner = (
  choices: ConsentChoices,
  { trackerExpected }: { trackerExpected: boolean },
): void => {
  if (snapshot.config.mode !== "OptIn") return;

  // A decision still waiting from earlier is superseded, not lost: persist it
  // first so the "before" state below is what the visitor actually had.
  flushPendingPersist?.();

  const before = snapshot.state;
  const withdrawnVisitorId =
    before.analytics && !choices.analytics
      ? readVisitorId() || undefined
      : undefined;

  applyDecisionInMemory(choices);

  let persisted = false;
  const win = consentWindow();

  const persist = () => {
    if (persisted) return;
    persisted = true;
    flushPendingPersist = undefined;
    win?.removeEventListener?.("pagehide", persist);
    persistDecision(choices, "banner", before);
  };

  if (!trackerExpected) {
    // Hand it over if the tracker happens to be loaded, then persist.
    forwardDecisionToTracker(choices, { withdrawnVisitorId });
    persist();
    return;
  }

  flushPendingPersist = () => {
    cancelPendingDelivery?.();
    persist();
  };
  win?.addEventListener?.("pagehide", persist);
  deliverDecisionToTracker(choices, { withdrawnVisitorId }, persist);
};

/** Opens the banner's settings view ("Cookie settings"). OptIn + DappleOS only. */
export const openConsentSettings = (): void => {
  const { mode, banner } = snapshot.state;
  if (mode !== "OptIn" || banner !== "DappleOS") return;
  setSnapshot({ settingsOpen: true });
};

/** Closes the settings view without recording anything. */
export const closeConsentSettings = (): void => {
  setSnapshot({ settingsOpen: false });
};

const getSnapshot = () => snapshot;

/** Subscribes a component to the whole snapshot. */
export const useConsentSnapshot = (): ConsentStoreSnapshot =>
  useSyncExternalStore(subscribeConsent, getSnapshot, getSnapshot);

/** Subscribes a component to the consent answer. */
export const useConsent = (): ConsentState => useConsentSnapshot().state;

/**
 * Test seam: rebuilds the store from the current window, cookie and storage,
 * forgetting any in-memory decision. Every test file that touches consent
 * calls this in `beforeEach`, since the store is module state.
 */
export const resetConsentStoreForTests = (): void => {
  cancelPendingDelivery?.();
  flushPendingPersist = undefined;
  stopListening();
  initialise();
  notify();
};
